import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { useMutation } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { CustomerProfileIframe } from '../../components/customer-profile-iframe';
import { SideDrawer } from '../../components/side-drawer';
import { CustomerData, CustomerPaymentData } from '../../types';
import { captureError } from '../../utils/capture-error';

export function RegisterPaymentAdd(props: {
  onUpdateBilling: (
    data: CustomerData,
    customerPaymentProfileId?: CustomerPaymentData['customerPaymentProfileId'],
  ) => void;
  isAgent?: boolean;
  // Guest email
  email?: string;
  // Agent button settings
  buttonLarge?: boolean;
  buttonText?: string;
}) {
  // State
  const [open, setOpen] = React.useState(false);
  const [token, setToken] = React.useState('');
  const [customerProfileId, setCustomerProfileId] = React.useState('');

  React.useEffect(() => {
    // Reset State
    setToken('');
    setCustomerProfileId('');
  }, [open]);

  // Mutation - Get Authorize.Net token for payment profile
  const mutateAuthorize = useMutation({
    mutationFn: async () => {
      const hostedIframeReturnUrl =
        window.location.origin + '/IFrameCommunicator.html';

      if (props.isAgent) {
        // Get a token for an agent allowing them to add payment information
        const response: { token: string } = await API.post(
          'EventsAPI',
          '/events/payments/customer-profile/token',
          { body: { hostedIframeReturnUrl } },
        );
        setToken(response.token);
      } else {
        // Create a profile for a guest
        const responseProfile: { customerProfileId: string } = await API.post(
          'EventsAPI',
          '/events/payments/customer-profile/create-guest',
          { body: { email: props.email } },
        );
        setCustomerProfileId(responseProfile.customerProfileId);

        // Get a token for a guest allowing them to add payment information
        const responseToken: { token: string } = await API.post(
          'EventsAPI',
          '/events/payments/customer-profile/token',
          {
            body: {
              customerProfileId: responseProfile.customerProfileId,
              hostedIframeReturnUrl,
            },
          },
        );
        setToken(responseToken.token);
      }
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Mutation - Get Customer Profile Information
  const mutateProfile = useMutation({
    mutationFn: async () => {
      const response: { data: CustomerData } = await API.post(
        'EventsAPI',
        '/events/payments/customer-profile',
        { body: props.isAgent ? {} : { customerProfileId } },
      );
      props.onUpdateBilling(response.data, customerProfileId);
    },
    onError: (error) => captureError({ data: { error } }),
  });

  const loading = mutateAuthorize.isLoading || mutateProfile.isLoading;

  const handleRefetchAndClose = async () => {
    if (!loading) {
      await mutateProfile.mutateAsync();
      setOpen(false);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
    mutateAuthorize.mutate();
  };

  let dialogContent = <div />;
  if (loading) {
    dialogContent = (
      <div style={{ padding: 40 }}>
        <LinearProgress />
      </div>
    );
  } else if (token) {
    dialogContent = (
      <Box
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <CustomerProfileIframe
          action="/customer/addPayment"
          iframeToken={token}
          title="Add Card"
          onReceiveCommunication={(action) => {
            if (action) {
              handleRefetchAndClose();
            }
          }}
        />
      </Box>
    );
  }

  return (
    <React.Fragment>
      {props.isAgent ? (
        <Button
          size={props.buttonLarge ? 'large' : 'small'}
          fullWidth={props.buttonLarge}
          color={props.buttonLarge ? 'primary' : 'inherit'}
          variant="contained"
          disableElevation
          onClick={handleClickOpen}
        >
          {props.buttonText || 'Add Card'}
        </Button>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <Button
            disabled={!props.email}
            size="large"
            variant="contained"
            disableElevation
            onClick={handleClickOpen}
            style={{ width: '100%', maxWidth: 400 }}
          >
            Open payment information form
          </Button>
        </div>
      )}

      <SideDrawer
        title="Add Card"
        disabled={loading}
        open={open}
        onClose={handleRefetchAndClose}
      >
        {dialogContent}
      </SideDrawer>
    </React.Fragment>
  );
}
